'use client';

import React, { useState } from 'react';
import { Settings2 } from 'lucide-react';
import {
  usePageLayoutPreferences,
  type PageLayoutPreferences,
  type PagePreferenceScope,
} from '../hooks/use-page-layout-preferences';
import { Button } from './button';
import { Sheet } from './sheet';
import { useCompositionDensity } from './composition-density';

export interface CustomisePageItem {
  id: string;
  label: string;
  kind: 'section' | 'column';
  defaultVisible?: boolean;
  protected?: boolean;
}
export interface CustomisePageProps {
  items: readonly CustomisePageItem[];
  scope: PagePreferenceScope;
  preferences?: PageLayoutPreferences;
  /** Used by PageHeader's single overflow item. */
  open: boolean;
  onClose: () => void;
}

export function isPageItemVisible(
  item: CustomisePageItem,
  values: PageLayoutPreferences['values'],
): boolean {
  return item.protected || (values[item.id] ?? item.defaultVisible ?? true);
}

/** Mount once, using PageHeader.customise for its only trigger. */
export function CustomisePage({ items, scope, preferences, open, onClose }: CustomisePageProps) {
  const fallback = usePageLayoutPreferences(scope, 'visibility', !preferences);
  const expandedFallback = usePageLayoutPreferences(scope, 'expanded', !preferences);
  const store = preferences ?? fallback;
  const density = useCompositionDensity();
  const [undo, setUndo] = useState<{ id: string; visible: boolean } | null>(null);
  const hidden = items.filter((item) => !isPageItemVisible(item, store.values)).length;
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Customise page"
      description={store.ready ? `${hidden} hidden` : 'Loading layout preferences'}
      footer={
        <>
          {undo && (
            <Button
              type="button"
              variant="secondary"
              density={density}
              onClick={() => {
                store.set(undo.id, undo.visible);
                setUndo(null);
              }}
            >
              Undo
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            density={density}
            disabled={!store.ready}
            onClick={() => {
              store.reset();
              expandedFallback.reset();
              setUndo(null);
            }}
          >
            Reset layout
          </Button>
        </>
      }
    >
      {store.ready && (
        <div className="space-y-6">
          {(['section', 'column'] as const).map((kind) => (
            <fieldset key={kind} className="min-w-0 space-y-2">
              <legend className="font-body text-card-title text-text-heading">
                {kind === 'section' ? 'Sections' : 'Columns'}
              </legend>
              {items
                .filter((item) => item.kind === kind)
                .map((item) => (
                  <label
                    key={item.id}
                    className={`flex min-w-0 items-center gap-2 font-body text-body text-text-body ${density === 'comfort' ? 'min-h-12' : 'min-h-11'}`}
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-action"
                      checked={isPageItemVisible(item, store.values)}
                      disabled={item.protected}
                      onChange={(event) => {
                        if (item.protected) return;
                        const next = event.target.checked;
                        setUndo({ id: item.id, visible: !next });
                        store.set(item.id, next);
                      }}
                    />
                    <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                      {item.label}
                      {item.protected && (
                        <>
                          {' '}
                          <span className="text-text-muted">· Protected</span>
                        </>
                      )}
                    </span>
                  </label>
                ))}
            </fieldset>
          ))}
        </div>
      )}
    </Sheet>
  );
}

export const CustomisePageIcon = Settings2;
